import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  ExternalDataService,
  Customer,
  CustomerAddress,
  CreateAppointment,
  Zone,
} from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-appointment-create',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './appointment-create.component.html',
  styleUrl: './appointment-create.component.css',
})
export class AppointmentCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  customerId: string = '';
  customer: Customer | null = null;
  appointmentForm!: FormGroup;
  selectedAddress: CustomerAddress | null = null;
  loading = false;
  loadingCustomer = false;
  creatingAppointment = false;
  errorMessage = '';
  successMessage = '';
  // Time slot options
  timeSlotOptions: string[] = [];
  zones: Zone[] = [];

  constructor(
    private fb: FormBuilder,
    private externalDataService: ExternalDataService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Get customerId from route
    this.customerId = this.route.snapshot.paramMap.get('customerId') || '';

    if (!this.customerId) {
      this.errorMessage = 'Customer ID is required.';
      return;
    }

    this.initializeForm();
    this.loadGlobalSettings();
    this.loadZonesAndCustomer();
  }

  loadZonesAndCustomer(): void {
    this.loadingCustomer = true;
    this.externalDataService
      .getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones;
          this.loadCustomer();
        },
        error: (error) => {
          console.error('Error loading zones:', error);
          this.loadCustomer(); // Still try to load customer
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadGlobalSettings(): void {
    this.externalDataService
      .getGlobalSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          if (
            settings &&
            settings.appointmentTimeSlots &&
            settings.appointmentTimeSlots.length > 0
          ) {
            this.timeSlotOptions = settings.appointmentTimeSlots;
          } else {
            // Fallback if empty
            this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
          }
        },
        error: (error) => {
          console.error('Error loading global settings:', error);
          // Fallback
          this.timeSlotOptions = ['8 AM – 1 PM', '1 PM – 5 PM'];
        },
      });
  }

  loadCustomer(): void {
    this.loadingCustomer = true;
    this.errorMessage = '';

    this.authService
      .getMsalInitializedObservable()
      .pipe(
        filter((initialized) => initialized),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // Try to get customer by ID directly first
        this.externalDataService
          .getCustomerById(this.customerId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (customer) => {
              this.customer = customer;
              // initializeForm() is now called in ngOnInit, but we might need to re-patch values
              // if customer data is essential for initial form setup.
              // For now, assuming initializeForm() sets up structure, and selectAddress patches values.
              this.loadingCustomer = false;

              // If customer has a primary address, load holidays for it
              const primaryAddress =
                this.customer.customerAddress?.find((addr) => addr.isPrimary) ||
                this.customer.customerAddress?.[0];
              if (primaryAddress) {
                this.selectAddress(primaryAddress);
              }
            },
            error: () => {
              // Fallback: search through customer list
              this.externalDataService
                .getCustomers(0, 1000)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (response) => {
                    const customer = response.items.find(
                      (c) => c.customerId === this.customerId,
                    );

                    if (customer) {
                      this.customer = customer;
                      // initializeForm() is now called in ngOnInit
                      const primaryAddress =
                        this.customer.customerAddress?.find(
                          (addr) => addr.isPrimary,
                        ) || this.customer.customerAddress?.[0];
                      if (primaryAddress) {
                        this.selectAddress(primaryAddress);
                      }
                    } else {
                      console.error(
                        'Customer not found. CustomerId:',
                        this.customerId,
                      );
                      this.errorMessage = 'Customer not found.';
                    }
                    this.loadingCustomer = false;
                  },
                  error: (listError) => {
                    console.error('Error loading customer list:', listError);
                    this.errorMessage = 'Failed to load customer information.';
                    this.loadingCustomer = false;
                  },
                });
            },
          });
      });
  }

  initializeForm(): void {
    this.appointmentForm = this.fb.group({
      date: ['', Validators.required],
      timeSlot: ['', Validators.required],
      address: ['', Validators.required],
      addressId: [''],
      location: this.fb.group({
        latitude: [0, Validators.required],
        longitude: [0, Validators.required],
      }),
      locationName: ['', Validators.required],
      geoZoneId: ['', Validators.required],
      pincode: ['', Validators.required],
      remarks: [''],
    });

    // Set default geoZoneId if available
    if (
      this.customer?.customerAddress &&
      this.customer.customerAddress.length > 0
    ) {
      // You might want to get geoZoneId from the primary address or first address
      const primaryAddress =
        this.customer.customerAddress.find((addr) => addr.isPrimary) ||
        this.customer.customerAddress[0];
      if (primaryAddress) {
        this.selectAddress(primaryAddress);
      }
    }
  }

  selectAddress(address: CustomerAddress): void {
    this.selectedAddress = address;

    // Resolve geoZoneId by checking which zone contains the location coordinates
    let geoZoneId = '';
    const lat = address.location.latitude;
    const lng = address.location.longitude;

    if (this.zones && this.zones.length > 0) {
      const containingZone = this.zones.find((zone) => {
        if (
          zone.coordinate &&
          zone.coordinate.coordinates &&
          zone.coordinate.coordinates.length > 0
        ) {
          return this.isPointInPolygon(lat, lng, zone.coordinate.coordinates);
        }
        return false;
      });

      if (containingZone) {
        geoZoneId = containingZone.id;
      }
    }

    // Fallback if no zone found
    if (!geoZoneId) {
      geoZoneId = address.locationName;
    }

    this.appointmentForm.patchValue({
      address: address.address,
      addressId: address.addressId,
      location: {
        latitude: lat,
        longitude: lng,
      },
      locationName: address.locationName,
      geoZoneId: geoZoneId,
      pincode: address.pincode,
    });
  }

  /**
   * Ray-casting algorithm to check if a point is inside a polygon
   */
  private isPointInPolygon(
    lat: number,
    lng: number,
    polygon: { latitude: number; longitude: number }[],
  ): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].latitude,
        yi = polygon[i].longitude;
      const xj = polygon[j].latitude,
        yj = polygon[j].longitude;

      const intersect =
        yi > lng !== yj > lng &&
        lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  onSubmit(): void {
    if (this.appointmentForm.valid && this.selectedAddress) {
      this.loading = true;
      this.errorMessage = '';

      const formValue = this.appointmentForm.value;

      // Use the selected date
      const dateTime = new Date(formValue.date);

      const appointmentData: CreateAppointment = {
        date: dateTime.toISOString(),
        address: formValue.address,
        addressId: formValue.addressId,
        location: formValue.location,
        locationName: formValue.locationName,
        geoZoneId: formValue.geoZoneId,
        pinCode: formValue.pincode,
        remarks: formValue.remarks || undefined,
        timeSlot: formValue.timeSlot || undefined,
      };

      this.creatingAppointment = true;
      this.errorMessage = '';
      this.successMessage = '';

      this.externalDataService
        .createAppointment(this.customerId, appointmentData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.creatingAppointment = false;
            this.successMessage = 'Appointment created successfully!';
            // Redirect to customers page after 2 seconds
            setTimeout(() => {
              this.router.navigate(['/customers']);
            }, 2000);
          },
          error: (error) => {
            this.creatingAppointment = false;
            console.error('Error creating appointment:', error);
            this.errorMessage =
              error.error?.message ||
              'Failed to create appointment. Please try again.';
          },
        });
    }
  }

  goBack(): void {
    this.router.navigate(['/customers']);
  }

  get customerAddresses(): CustomerAddress[] {
    return this.customer?.customerAddress || [];
  }

  isAddressSelected(address: CustomerAddress): boolean {
    return this.selectedAddress?.addressId === address.addressId;
  }

  getCurrentDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }
}
